<script setup lang="ts">
import StoreBalance from "./StoreBalance.vue";
import DebugOrderActions from "./DebugOrderActions.vue";
import { Eye, CheckCircle, XCircle, Printer, Info } from "lucide-vue-next";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogScrollContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { formatDate } from "@lib/utils/formatDate";
import { storeOrdersApiClient } from "@api-client/store_orders";
import type { ResType } from "@api-client/typeUtils";
import { isStoreOrderAllowedToCancel, isStoreOrderAllowedToAccept, STORE_ORDER_STATUS_OBJ, isStoreOrderAllowedStatusChange } from "@lib/types/storeOrderStatus";
import { ref, computed } from "vue";
import { toast } from "@iframe-widgets/pages/widgets/toast/iframeToast";
import AcceptOrderModal from "./AcceptOrderModal.vue";
import { useDialog } from "@/ui-plus/dialog/use-dialog";
import { printOrder } from "./printOrder";
import ShowStoreOrder from "@/orders-manager/order-modal/ShowStoreOrder.vue";
import PrintOptionsDropdown from "./PrintOptionsDropdown.vue";
import {
    STRIPE_PAYMENT_TYPE_ID,
    PAYPAL_PAYMENT_TYPE_ID,
    S_API_KEY_PAYMENT_TYPE_ID
} from "@lib/consts/paymentIds";

const dialog = useDialog();

const ApiPath = storeOrdersApiClient[":storeId"].get_by_order_ids.$post;
type OrderType = ResType<typeof ApiPath>["data"];

const props = defineProps<{
    order: OrderType[number];
    cachedStoreOrders: Record<string, OrderType[number]>;
}>();

const order = computed(
    () => props.cachedStoreOrders[props.order.storeOrderId] ?? props.order,
);

const showRefundInfo = computed(() => {
    const o = order.value;
    return (
        o.paymentMethodJSON?.id === STRIPE_PAYMENT_TYPE_ID ||
        o.paymentMethodJSON?.id === PAYPAL_PAYMENT_TYPE_ID ||
        o.paymentMethodJSON?.id === S_API_KEY_PAYMENT_TYPE_ID
    );
});

const browserLocale = window.navigator.language;

let isAcceptingOrder = ref(false);
const handleAcceptApi = async (storeOrderId: string, storeId: string, minutes: number | undefined) => {
    if (isAcceptingOrder.value) {
        return;
    }
    isAcceptingOrder.value = true;
    const { error } = await (await storeOrdersApiClient[":storeId"].accept_order.$post({
        param: { storeId },
        json: { storeOrderId, estimatedTimeInMinutes: minutes }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error accepting order");
    } else {
        toast.message(`Order accepted`);
        isStoreOrderAllowedStatusChange(props.cachedStoreOrders[storeOrderId].orderStatus, STORE_ORDER_STATUS_OBJ.ACCEPTED);
        props.cachedStoreOrders[storeOrderId].orderStatus = STORE_ORDER_STATUS_OBJ.ACCEPTED;
        if (minutes) {
            props.cachedStoreOrders[storeOrderId].estimatedTimeJSON = {
                min: minutes,
                unit: 'minutes'
            }
        }
        // Update the timestamp to prevent the periodic refresh from reloading this order
        props.cachedStoreOrders[storeOrderId].updated_at_ts = Date.now();
    }
    isAcceptingOrder.value = false;
}
const handleAccept = async (storeOrderId: string, storeId: string) => {
    dialog.show<number | undefined>({
        title: "Accept Order",
        closeable: true,
        component: AcceptOrderModal,
        onSuccess: async (minutes) => {
            await handleAcceptApi(storeOrderId, storeId, minutes);
        },
        onError: async (error) => {
            console.log(error);
        }
    });

}

let isCancellingOrder = ref(false);
const handleCancel = async (storeOrderId: string, storeId: string) => {
    if (isCancellingOrder.value) {
        return;
    }
    isCancellingOrder.value = true;
    const order = props.cachedStoreOrders[storeOrderId];
    // 🚨Every month 2% of your orders can be cancelled free🚨 after that we may still charge you for the service fees.
    // ${order.paymentMethodJSON?.isOnline ? '\n🚨THIS ORDER HAS ONLINE PAYMENT🚨\n🚨It will be refunded to customer🚨\n' : ''}

    if (!confirm(`Are you sure you want to cancel this order?

If this order was paid online, the customer will be refunded.

🚨This action cannot be undone🚨`)) {
        isCancellingOrder.value = false;
        return;
    }

    // Implement cancel logic
    const { error, data } = await (await storeOrdersApiClient[":storeId"].cancel_order.$post({
        param: { storeId },
        json: { storeOrderId }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error cancelling order");
    } else {
        toast.message(`Order cancelled ${order.paymentMethodJSON?.isOnline ? 'and refunded!' : ''}`);
        isStoreOrderAllowedStatusChange(props.cachedStoreOrders[storeOrderId].orderStatus, STORE_ORDER_STATUS_OBJ.CANCELLED);
        props.cachedStoreOrders[storeOrderId].orderStatus = STORE_ORDER_STATUS_OBJ.CANCELLED;
        // Update the timestamp to prevent the periodic refresh from reloading this order
        props.cachedStoreOrders[storeOrderId].updated_at_ts = Date.now();
        setTimeout(() => {
            if (data?.isTest) {
                toast.message(`System assumes this is a test order since the order was placed and cancelled by the same user. Fees added back to your store balance`);
            } else {
                if (data?.isFeeRefunded) {
                    toast.message(`Cancelled with no fee. Fees added back to your store balance, use cancellation wisely, only 5% of orders can be cancelled free`);
                } else {
                    toast.message(`${data?.cancelPercentage}% of orders were cancelled in the last 30 days, fees not refunded! (up to 5% of orders can be cancelled free)`);
                }
            }
        }, 1000);
    }
    isCancellingOrder.value = false;
}

const IS_DEV = import.meta.env.DEV;

</script>
<template>
    <Card class="p-2">
        <CardContent class="p-0">
            <div class="flex flex-col md:flex-row justify-between gap-4">
                <Dialog>
                    <DialogScrollContent class="max-w-xl">
                        <DialogHeader>
                            <DialogTitle></DialogTitle>
                            <DialogDescription />
                            <div class="flex gap-2 justify-between">
                                <Button class="w-full text-xl font-bold animate__tada"
                                        :disabled="isAcceptingOrder"
                                        v-if="isStoreOrderAllowedToAccept(order.orderStatus)"
                                        @click="handleAccept(order.storeOrderId, order.storeId)">
                                    <CheckCircle class="h-4 w-4 mr-1" />
                                    Accept
                                </Button>
                            </div>
                            <div class="flex gap-1 items-center">
                                <Button variant="secondary"
                                        class="w-full text-xl font-bold"
                                        @click="printOrder(order, browserLocale)">
                                    <Printer class="mr-1" />
                                    Print
                                </Button>
                                <PrintOptionsDropdown />
                            </div>
                        </DialogHeader>

                        <DebugOrderActions v-if="IS_DEV"
                                           :storeOrderId="order.storeOrderId" />

                        <ShowStoreOrder :orderDetails="order" />

                        <div class="flex gap-2 justify-between">
                            <Button class="w-full animate__tada"
                                    :disabled="isAcceptingOrder"
                                    v-if="isStoreOrderAllowedToAccept(order.orderStatus)"
                                    @click="handleAccept(order.storeOrderId, order.storeId)">
                                <CheckCircle class="h-4 w-4 mr-1" />
                                Accept
                            </Button>
                            <!-- <br>
                                            order.userId: {{ order.userId }}
                                            <br>
                                            user.id: {{ user?.id }}
                                            <br> -->
                            <div class="flex gap-2 items-center">
                                <DropdownMenu v-if="isStoreOrderAllowedToCancel(order.orderStatus)">
                                    <DropdownMenuTrigger as-child>
                                        <Button variant="destructive"
                                                size="sm">
                                            <XCircle /> {{ showRefundInfo ? 'Refund' : 'Cancel' }} Order
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        <DropdownMenuItem :disabled="isCancellingOrder"
                                                          @click="handleCancel(order.storeOrderId, order.storeId)"
                                                          class="bg-destructive text-destructive-foreground">
                                            <div>
                                                <p class="text-2xl font-bold">Are you sure?</p>
                                            </div>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Popover v-if="isStoreOrderAllowedToCancel(order.orderStatus)">
                                    <PopoverTrigger as-child>
                                        <Info class="inline-block" />
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <div class="space-y-2">
                                            <p class="text-2xl font-bold">Are you sure to cancel?</p>
                                            <p class="text-xs my-4">
                                                If it was a test order,
                                                <br>
                                                make sure to cancel it in the same browser.
                                                <br>
                                                AutoPrint app can not detect test orders.
                                            </p>
                                            <p class="text-xs my-4">
                                                Use cancellation wisely,
                                                <br>
                                                only 5% of orders can be cancelled free.
                                            </p>
                                            <p class="text-xs my-4">
                                                Fees will not be refunded
                                                <br>
                                                if more than 5% of orders were
                                                <br>
                                                cancelled in the last 30 days.
                                            </p>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <a class="text-xs underline"
                               v-if="!IS_DEV && order.paymentMethodJSON?.isOnline &&
                                order.stripeStoreConnectAccountId &&
                                order.stripePaymentIntentId"
                               :href="`https://dashboard.stripe.com/${order.stripeStoreConnectAccountId}/payments/${order.stripePaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                            <a class="text-xs underline"
                               v-if="IS_DEV && order.paymentMethodJSON?.isOnline &&
                                order.stripeStoreConnectAccountId &&
                                order.stripePaymentIntentId"
                               :href="`https://dashboard.stripe.com/acct_1RIvHDGftdUvJams/test/connect/accounts/${order.stripeStoreConnectAccountId}/payments/${order.stripePaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                            <a class="text-xs underline"
                               v-if="IS_DEV && order.sApiKey && order.sApiKey.sApiKeyPaymentIntentId"
                               :href="`https://dashboard.stripe.com/acct_1UC3b5K6oPMjQGYC/test/payments/${order.sApiKey.sApiKeyPaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                        </div>

                        <Dialog>
                            <DialogTrigger>
                                <Button variant="outline"
                                        size="lg">
                                    Balance and Transactions
                                </Button>
                            </DialogTrigger>
                            <DialogScrollContent>
                                <DialogHeader>
                                    <DialogTitle>Balance and Transactions</DialogTitle>
                                    <DialogDescription>
                                        Show the current balance and transactions for this order.
                                    </DialogDescription>
                                </DialogHeader>
                                <StoreBalance :currentStoreId="order.storeId"
                                              :searchQuery="order.storeOrderId" />
                            </DialogScrollContent>
                        </Dialog>

                    </DialogScrollContent>

                    <!-- Order Info -->
                    <DialogTrigger as-child
                                   class="w-full cursor-pointer">
                        <div class="space-y-2 flex-grow hover:bg-secondary/50 transition-colors p-2 rounded-md ">
                            <div class="flex flex-wrap items-center gap-2">
                                <h3 class="text-lg font-bold">#{{ order.shortOtp || 'N/A' }}</h3>
                                <Badge
                                       :variant="`${order.orderStatus === STORE_ORDER_STATUS_OBJ.CANCELLED ? 'destructive' : 'secondary'}`">
                                    {{ order.orderStatus }}
                                </Badge>
                                <Badge variant="secondary"
                                       class="capitalize">
                                    {{ order.orderType || 'N/A' }}
                                </Badge>
                                <div v-if="order.scheduled_at_ts">
                                    <Badge variant="secondary"
                                           class="capitalize text-xs">
                                        SCHEDULED
                                    </Badge>
                                </div>
                                <span v-if="order.isCancelled"
                                      class="text-xs border border-destructive-foreground p-1 rounded-md inline-block m-1 bg-destructive text-destructive-foreground underline">
                                    Order CANCELLED
                                </span>
                            </div>
                            <div class="text-left text-xs text-muted-foreground">
                                <p>
                                    {{
                                        order.deliveryAddressJSON?.shippingName ||
                                        order.pickupNameSurname ||
                                        order.userName ||
                                        order.userEmail || 'unknown'
                                    }}
                                </p>
                                <p>
                                    {{ formatDate(order.order_placed_at_ts, undefined, true) }}
                                </p>
                                <p v-if="order.order_placed_at_ts !== order.draft_create_at_ts">
                                    Draft: {{ formatDate(order.draft_create_at_ts, undefined, true) }}
                                </p>
                                <p v-if="order.scheduled_at_ts">
                                    Scheduled: {{ formatDate(order.scheduled_at_ts!, undefined, true) }}
                                </p>
                                <p>
                                    {{ order.storeName }}
                                    ({{ order.storeAddress1 }})
                                </p>
                            </div>
                        </div>
                    </DialogTrigger>

                    <!-- Actions -->
                    <div class="flex flex-wrap gap-2 items-center">
                        <div class="flex gap-2"
                             v-if="isStoreOrderAllowedToAccept(order.orderStatus)">
                            <Button size="sm"
                                    class="animate__tada"
                                    :disabled="isAcceptingOrder"
                                    @click="handleAccept(order.storeOrderId, order.storeId)">
                                <CheckCircle class="mr-1" />
                                Accept
                            </Button>
                        </div>

                        <Button variant="secondary"
                                size="sm"
                                @click="printOrder(order, browserLocale)">
                            <Printer class="mr-1" />
                            Print
                        </Button>

                        <DialogTrigger>
                            <Button variant="outline"
                                    size="sm">
                                Show
                            </Button>
                        </DialogTrigger>

                        <Popover v-if="IS_DEV">
                            <PopoverTrigger as-child>
                                <Button variant="ghost"
                                        size="sm">
                                    <Eye class="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent class="max-w-xl w-full max-h-96 overflow-y-auto">
                                <pre class="text-xs">{{ order }}</pre>
                            </PopoverContent>
                        </Popover>
                    </div>
                </Dialog>
            </div>
        </CardContent>
    </Card>
</template>